import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  LuCheck,
  LuChevronDown,
  LuFileText,
  LuImage,
  LuPenLine,
  LuPlus,
  LuTrash2,
  LuX,
} from "react-icons/lu";
import i18n from "i18next";
import { isVideoUrl, styleThumb } from "./videoStyleThumbs";
import SceneAudioService from "../../../services/sceneAudioService";
import { isFileTooLarge, fileTooLargeMessage } from "../../../../common/upload_limits.es6";

const CUSTOM_STYLE_ID = "__custom__";
export { CUSTOM_STYLE_ID };

const ASPECT_RATIOS = [
  { id: "16:9", w: 16, h: 9 },
  { id: "9:16", w: 9, h: 16 },
  { id: "1:1", w: 1, h: 1 },
  { id: "4:3", w: 4, h: 3 },
];

// Aspect ratios each provider supports. Providers not listed fall back to the
// Google (Omni) default (16:9 and 9:16 only).
const DEFAULT_ASPECT_RATIO_IDS = ["16:9", "9:16"];
const PROVIDER_ASPECT_RATIO_IDS = {
  grok: ["16:9", "9:16", "1:1", "4:3"],
  seedance: ["16:9", "4:3", "1:1", "9:16"],
  ltx: ["16:9", "9:16"],
};
const getAllowedAspectRatioIds = (provider) =>
  PROVIDER_ASPECT_RATIO_IDS[provider] || DEFAULT_ASPECT_RATIO_IDS;

const DEFAULT_STYLES = [
  { id: "photo" },
  { id: "3d" },
  { id: "cinema" },
  { id: "motion" },
  { id: "anime" },
  { id: "watercolor" },
  { id: "illustration" },
  { id: "stopmotion" },
];

const AUTO_PROVIDER = "veo";

const MODEL_OPTIONS = [
  {
    id: "veo",
    recommended: true,
  },
  {
    id: "grok",
    recommended: false,
  },
  {
    // "veo" and "veo-fast" both run Gemini Omni since 2026-10-23 (only the
    // resolution differs), so the picker shows the single "Omni" option.
    id: "veo-fast",
    recommended: false,
    hidden: true,
  },
  {
    id: "seedance",
    recommended: false,
  },
  {
    id: "ltx",
    recommended: false,
  },
];

const MODEL_SHORT_LABELS = {
  veo: "Omni",
  "veo-fast": "Omni",
  grok: "Grok",
  seedance: "Seedance",
  ltx: "LTX",
};

const VideoAIGenerateTab = ({
  provider,
  onProviderChange,
  aspectRatio,
  onAspectRatioChange,
  durationMode,
  onDurationModeChange,
  videoDuration,
  onVideoDurationChange,
  videoStyle,
  onVideoStyleChange,
  videoStyles,
  stylesLoading,
  narrationText,
  onNarrationChange,
  disabled,
  customStyleDesc = "",
  onCustomStyleDescChange,
  promptMode: promptModeProp,
  onPromptModeChange,
  styleReferenceFile,
  onStyleReferenceChange,
  textOverlays,
  onTextOverlaysChange,
  navItemSelected,
  navId,
  scenes = [{ id: 1, text: "" }],
  onScenesChange,
  enrichTexts = false,
  onEnrichTextsChange,
}) => {
  const styleReferenceInputRef = useRef(null);
  const overlayImageInputRefs = useRef({});
  const [promptModeLocal, setPromptModeLocal] = useState("write");
  const promptMode = promptModeProp !== undefined ? promptModeProp : promptModeLocal;
  const setPromptMode = (mode) => {
    setPromptModeLocal(mode);
    onPromptModeChange?.(mode);
  };
  const [textInVideo, setTextInVideo] = useState(false);
  const [showTextInVideoAdvanced, setShowTextInVideoAdvanced] = useState(false);
  const [showAllStyles, setShowAllStyles] = useState(false);
  const [isAutoProvider, setIsAutoProvider] = useState(true);
  const [activeSceneIndex, setActiveSceneIndex] = useState(0);
  const [advancedOpenIds, setAdvancedOpenIds] = useState(new Set());
  const isCustomStyle = videoStyle === CUSTOM_STYLE_ID;

  const toggleAdvanced = (id) => {
    setAdvancedOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const activeProvider = isAutoProvider ? AUTO_PROVIDER : provider;

  // Keep the selected ratio valid for the active provider. If the current ratio
  // isn't allowed (e.g. switching to LTX while 1:1 was picked), snap to the first
  // supported one so only provider-allowed ratios can stay selected.
  useEffect(() => {
    const allowed = getAllowedAspectRatioIds(activeProvider);
    if (!allowed.includes(aspectRatio)) {
      onAspectRatioChange?.(allowed[0]);
    }
  }, [activeProvider, aspectRatio, onAspectRatioChange]);

  const selectAutoMode = () => {
    if (isAutoProvider) return;
    setIsAutoProvider(true);
    onProviderChange?.(AUTO_PROVIDER);
  };

  const selectCustomMode = () => {
    if (!isAutoProvider) return;
    setIsAutoProvider(false);
  };

  const addScene = () => {
    const newScene = { id: Date.now(), text: "" };
    const updated = [...scenes, newScene];
    onScenesChange?.(updated);
    setActiveSceneIndex(updated.length - 1);
  };

  const removeScene = (idx) => {
    if (scenes.length <= 1) return;
    const updated = scenes.filter((_, i) => i !== idx);
    onScenesChange?.(updated);
    setActiveSceneIndex((prev) => (prev >= updated.length ? updated.length - 1 : prev));
  };

  const updateSceneText = (idx, text) => {
    const updated = [...scenes];
    updated[idx] = { ...updated[idx], text };
    onScenesChange?.(updated);
  };

  // Resolution based on aspect ratio (actual video generation resolution)
  // Backend generates at these resolutions, then FFmpeg overlay uses them
  const getResolution = () => {
    if (aspectRatio === "16:9") return { width: 1280, height: 720 };
    if (aspectRatio === "9:16") return { width: 720, height: 1280 };
    if (aspectRatio === "1:1") return { width: 1024, height: 1024 };
    if (aspectRatio === "4:3") return { width: 1024, height: 768 };
    return { width: 1280, height: 720 }; // default
  };

  const resolution = getResolution();

  // Preview scale factor (to fit in UI)
  // Display preview at 50% of actual video size
  const previewScale = 0.5;

  const resolvePositionLabel = (position) => {
    const map = {
      "top-left": i18n.t("FileModal.APIProviders.VideoGeneration.TopLeft"),
      "top-center": i18n.t("FileModal.APIProviders.VideoGeneration.TopCenter"),
      "top-right": i18n.t("FileModal.APIProviders.VideoGeneration.TopRight"),
      "bottom-left": i18n.t("FileModal.APIProviders.VideoGeneration.BottomLeft"),
      "bottom-center": i18n.t("FileModal.APIProviders.VideoGeneration.BottomCenter"),
      "bottom-right": i18n.t("FileModal.APIProviders.VideoGeneration.BottomRight"),
      center: i18n.t("FileModal.APIProviders.VideoGeneration.Center"),
    };
    return map[position] || position;
  };

  // Modern input/select style
  const inputStyle = {
    width: "100%",
    padding: "8px 12px",
    fontSize: "13px",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    background: "#fff",
    color: "#1f2937",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
  };

  const selectStyle = {
    ...inputStyle,
    cursor: "pointer",
    appearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23475569' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
    paddingRight: "32px",
  };

  const handleFocus = (e) => {
    e.target.style.borderColor = "#3b82f6";
    e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
  };

  const handleBlur = (e) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
  };

  const styles = useMemo(() => {
    if (videoStyles?.length) {
      return videoStyles.map((style) => ({
        id: style.styleKey,
        label: style.name || style.promptText || style.styleKey || style.id,
        image:
          style.sampleVideoUrl ||
          style.thumbnail ||
          style.sampleImageUrl ||
          styleThumb(style.styleKey) ||
          null,
      }));
    }
    return DEFAULT_STYLES.map((style) => ({
      id: style.id,
      label: style.id,
      image: null,
    }));
  }, [videoStyles]);

  return (
    <div
      className={`add-video-modal__ai-tab${disabled ? " add-video-modal__ai-tab--disabled" : ""}`}
    >
      <div className="add-video-modal__ai-grid">
        <div className="add-video-modal__ai-section">
          <div className="add-video-modal__ai-label">
            {i18n.t("FileModal.APIProviders.ImageStyles.AIProvider")}
          </div>
          <div
            className="add-video-modal__ai-mode-toggle"
            role="tablist"
            aria-label={i18n.t("FileModal.APIProviders.ImageStyles.AIProvider")}
          >
            <button
              type="button"
              role="tab"
              aria-selected={isAutoProvider}
              className={`add-video-modal__ai-mode-toggle-btn${
                isAutoProvider ? " add-video-modal__ai-mode-toggle-btn--active" : ""
              }`}
              onClick={selectAutoMode}
            >
              <span>{i18n.t("FileModal.APIProviders.ImageStyles.AutoProvider")}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={!isAutoProvider}
              className={`add-video-modal__ai-mode-toggle-btn${
                !isAutoProvider ? " add-video-modal__ai-mode-toggle-btn--active" : ""
              }`}
              onClick={selectCustomMode}
            >
              <span>{i18n.t("FileModal.APIProviders.ImageStyles.CustomProvider")}</span>
            </button>
          </div>
          {!isAutoProvider && (
            <div className="add-video-modal__ai-provider-chips">
              {MODEL_OPTIONS.filter((option) => !option.hidden).map((option) => {
                const fullLabel =
                  i18n.t(`FileModal.APIProviders.VideoGeneration.Model.${option.id}.Label`) ||
                  option.id;
                const shortLabel = MODEL_SHORT_LABELS[option.id] || fullLabel;
                return (
                  <button
                    key={option.id}
                    type="button"
                    disabled={option.disabled}
                    aria-disabled={option.disabled || undefined}
                    className={`add-video-modal__ai-provider-chip${
                      provider === option.id ? " add-video-modal__ai-provider-chip--active" : ""
                    }${option.disabled ? " add-video-modal__ai-provider-chip--disabled" : ""}`}
                    onClick={() => {
                      if (option.disabled) return;
                      onProviderChange?.(option.id);
                    }}
                    title={fullLabel}
                    aria-label={fullLabel}
                  >
                    {shortLabel}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="add-video-modal__ai-section">
          <div className="add-video-modal__ai-label">
            {i18n.t("FileModal.APIProviders.VideoGeneration.Duration")}
          </div>
          <div className="add-video-modal__ai-duration">
            <div className="add-video-modal__ai-duration-buttons">
              <button
                type="button"
                className={`add-video-modal__ai-duration-btn${
                  durationMode === "auto" ? " add-video-modal__ai-duration-btn--active" : ""
                }`}
                onClick={() => onDurationModeChange("auto")}
              >
                <span>{i18n.t("FileModal.APIProviders.VideoGeneration.Auto")}</span>
              </button>
              {promptMode !== "write" && (
                <button
                  type="button"
                  className={`add-video-modal__ai-duration-btn${
                    durationMode === "custom" ? " add-video-modal__ai-duration-btn--active" : ""
                  }`}
                  onClick={() => onDurationModeChange("custom")}
                >
                  <span>{i18n.t("FileModal.APIProviders.VideoGeneration.Custom")}</span>
                </button>
              )}
            </div>
            {durationMode === "custom" && (
              <div className="add-video-modal__ai-duration-input-row">
                <input
                  type="number"
                  min="1"
                  value={videoDuration}
                  onChange={(event) => onVideoDurationChange(event.target.value)}
                  style={{
                    width: "180px",
                    padding: "8px 12px",
                    fontSize: "13px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    background: "#fff",
                    color: "#1f2937",
                    outline: "none",
                    flex: "none",
                  }}
                  placeholder={
                    i18n.t("FileModal.APIProviders.VideoGeneration.VideoDurationPlaceholder") ||
                    "20"
                  }
                />
                <span className="add-video-modal__ai-duration-unit">
                  {i18n.t("FileModal.APIProviders.VideoGeneration.Seconds")}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="add-video-modal__ai-section">
        <div className="add-video-modal__ai-label">
          {i18n.t("FileModal.APIProviders.VideoGeneration.Style")}
        </div>
        <div className="add-video-modal__ai-style-grid">
          {(showAllStyles ? styles : styles.slice(0, 5)).map((style) => (
            <button
              key={style.id}
              type="button"
              onClick={() => {
                if (styleReferenceFile) {
                  onStyleReferenceChange?.(null);
                }
                onVideoStyleChange(style.id);
              }}
              className={`add-video-modal__ai-style-card${
                videoStyle === style.id ? " add-video-modal__ai-style-card--active" : ""
              }`}
              style={{ position: "relative", overflow: "hidden", minHeight: "100px" }}
            >
              {style.image ? (
                React.createElement(isVideoUrl(style.image) ? "video" : "img", {
                  src: style.image,
                  ...(isVideoUrl(style.image)
                    ? { muted: true, loop: true, autoPlay: true, playsInline: true }
                    : { alt: "" }),
                  style: {
                    position: "absolute",
                    top: "6px",
                    left: "6px",
                    right: "6px",
                    width: "calc(100% - 12px)",
                    height: "72px",
                    objectFit: "cover",
                    borderRadius: "10px",
                  },
                })
              ) : (
                <span className="add-video-modal__ai-style-card-placeholder">{style.label}</span>
              )}
              <span
                style={{ position: "relative", zIndex: 1, marginTop: style.image ? "78px" : "0" }}
              >
                {style.label}
              </span>
              {videoStyle === style.id && (
                <div className="add-video-modal__ai-style-check">
                  <LuCheck size={12} />
                </div>
              )}
            </button>
          ))}

          {/* Style Reference card */}
          <input
            type="file"
            accept="image/*"
            ref={styleReferenceInputRef}
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              // 500 MB üst sınır guard'ı — aşan dosyayı kabul etme.
              if (isFileTooLarge(file)) {
                alert(fileTooLargeMessage(file));
                return;
              }
              onStyleReferenceChange?.(file);
            }}
          />
          <button
            type="button"
            onClick={() => styleReferenceInputRef.current?.click()}
            className={`add-video-modal__ai-style-card add-video-modal__ai-style-card--custom${
              styleReferenceFile ? " add-video-modal__ai-style-card--active" : ""
            }`}
            style={{ position: "relative", overflow: "hidden", minHeight: "100px" }}
          >
            {styleReferenceFile ? (
              <img
                src={URL.createObjectURL(styleReferenceFile)}
                alt="ref"
                style={{
                  position: "absolute",
                  top: "6px",
                  left: "6px",
                  right: "6px",
                  width: "calc(100% - 12px)",
                  height: "72px",
                  objectFit: "cover",
                  borderRadius: "10px",
                }}
              />
            ) : (
              <div className="add-video-modal__ai-style-custom-icon">
                <LuImage size={24} />
              </div>
            )}
            <span
              style={{
                position: "relative",
                zIndex: 1,
                marginTop: styleReferenceFile ? "78px" : "0",
              }}
            >
              {i18n.t("FileModal.APIProviders.VideoGeneration.StyleReference") || "Style Reference"}
            </span>
            {styleReferenceFile && (
              <div className="add-video-modal__ai-style-check">
                <LuCheck size={12} />
              </div>
            )}
          </button>

          {/* Custom Style card — always last */}
          <button
            type="button"
            onClick={() => {
              if (styleReferenceFile) {
                onStyleReferenceChange?.(null);
              }
              onVideoStyleChange(CUSTOM_STYLE_ID);
            }}
            className={`add-video-modal__ai-style-card add-video-modal__ai-style-card--custom${
              isCustomStyle ? " add-video-modal__ai-style-card--active" : ""
            }`}
          >
            <div className="add-video-modal__ai-style-custom-icon">
              <LuPenLine size={24} />
            </div>
            <span>
              {i18n.t("FileModal.APIProviders.VideoGeneration.CustomCardLabel") || "Custom"}
            </span>
            {isCustomStyle && (
              <div className="add-video-modal__ai-style-check">
                <LuCheck size={12} />
              </div>
            )}
          </button>
        </div>

        {isCustomStyle && (
          <div className="add-video-modal__ai-custom-desc">
            <label>{i18n.t("FileModal.APIProviders.VideoGeneration.CustomStyleDescription")}</label>
            <textarea
              value={customStyleDesc}
              onChange={(e) => onCustomStyleDescChange?.(e.target.value)}
              placeholder={i18n.t("FileModal.APIProviders.VideoGeneration.CustomStylePlaceholder")}
              rows={3}
            />
          </div>
        )}

        {styles.length > 5 && (
          <button
            type="button"
            className="add-video-modal__ai-style-more"
            onClick={() => setShowAllStyles(!showAllStyles)}
          >
            <LuChevronDown
              size={14}
              style={{
                transform: showAllStyles ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
            {showAllStyles
              ? i18n.t("FileModal.APIProviders.VideoGeneration.LessStyles") || "Less Styles"
              : i18n.t("FileModal.APIProviders.VideoGeneration.MoreStyles")}
            {!showAllStyles && <span>{styles.length - 5}+</span>}
          </button>
        )}
      </div>

      <div className="add-video-modal__ai-section">
        <div className="add-video-modal__ai-label">
          {i18n.t("OptionalEnhancements") || "Optional Enhancements"}
        </div>
        <div className="add-video-modal__ai-enhancements">
          <div className="add-video-modal__ai-enhance-card add-video-modal__ai-enhance-card--stacked">
            <div className="add-video-modal__ai-enhance-title">
              {i18n.t("FileModal.APIProviders.VideoGeneration.AspectRatio")}
            </div>
            <div className="add-video-modal__ai-ratio">
              {ASPECT_RATIOS.filter((ratio) =>
                getAllowedAspectRatioIds(activeProvider).includes(ratio.id)
              ).map((ratio) => {
                const maxSide = Math.max(ratio.w, ratio.h);
                const width = Math.max(12, Math.round((ratio.w / maxSide) * 22));
                const height = Math.max(12, Math.round((ratio.h / maxSide) * 22));
                return (
                  <button
                    key={ratio.id}
                    type="button"
                    onClick={() => onAspectRatioChange(ratio.id)}
                    className={`add-video-modal__ai-chip${
                      aspectRatio === ratio.id ? " add-video-modal__ai-chip--active" : ""
                    }`}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "6px",
                      minHeight: "52px",
                    }}
                  >
                    <span
                      className="add-video-modal__ai-chip-box"
                      style={{
                        width,
                        height,
                        marginTop: "auto",
                      }}
                    />
                    <span style={{ marginTop: "auto" }}>{ratio.id}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowTextInVideoAdvanced((prev) => !prev)}
          style={{
            marginTop: "12px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            background: "none",
            border: "none",
            padding: "4px 0",
            fontSize: "13px",
            fontWeight: 600,
            color: "#7c3aed",
            cursor: "pointer",
          }}
        >
          <LuChevronDown
            size={14}
            style={{
              transform: showTextInVideoAdvanced ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
          {i18n.t("FileModal.APIProviders.VideoGeneration.AdvancedSettings") || "Gelişmiş Ayarlar"}
        </button>
        {showTextInVideoAdvanced && (
          <div
            className={`add-video-modal__ai-enhance-card${
              textInVideo ? " add-video-modal__ai-enhance-card--active" : ""
            }`}
            style={{ marginTop: "12px" }}
          >
            <div className="add-video-modal__ai-enhance-info">
              <div className="add-video-modal__ai-enhance-title">{i18n.t("TextInVideo")}</div>
              <div className="add-video-modal__ai-enhance-subtitle">
                {i18n.t("FileModal.APIProviders.VideoGeneration.OverlayText")}
              </div>
            </div>
            <button
              type="button"
              className={`add-video-modal__ai-toggle${
                textInVideo ? " add-video-modal__ai-toggle--on" : ""
              }`}
              onClick={() => setTextInVideo((prev) => !prev)}
            >
              <span />
            </button>
          </div>
        )}
        {showTextInVideoAdvanced && textInVideo && (
          <div
            style={{
              marginTop: "16px",
              padding: "16px",
              background: "#f8fafc",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <div style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937" }}>
                {i18n.t("FileModal.APIProviders.VideoGeneration.TextAndImageOverlays")}
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  type="button"
                  onClick={() => {
                    const newOverlay = {
                      id: Date.now(),
                      type: "text",
                      text: "",
                      position: "bottom-left",
                      fontFamily: "DejaVu Sans",
                      fontStyle: "normal",
                      fontSize: 40, // For 1280x720 video
                      fontColor: "#FFFFFF",
                      backgroundColor: "#000000",
                      backgroundOpacity: 0.7,
                    };
                    onTextOverlaysChange?.([...(textOverlays || []), newOverlay]);
                  }}
                  style={{
                    padding: "6px 12px",
                    fontSize: "12px",
                    background: "#3b82f6",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <LuPlus size={14} /> {i18n.t("FileModal.APIProviders.VideoGeneration.Text")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const newOverlay = {
                      id: Date.now(),
                      type: "image",
                      imageUrl: "",
                      imageFile: null,
                      position: "top-right",
                      width: 180, // For 1280x720 video
                      height: 100, // For 1280x720 video
                      opacity: 0.95,
                    };
                    onTextOverlaysChange?.([...(textOverlays || []), newOverlay]);
                  }}
                  style={{
                    padding: "6px 12px",
                    fontSize: "12px",
                    background: "#10b981",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <LuPlus size={14} /> {i18n.t("FileModal.APIProviders.VideoGeneration.Image")}
                </button>
              </div>
            </div>

            {(!textOverlays || textOverlays.length === 0) && (
              <div
                style={{ textAlign: "center", padding: "24px", color: "#64748b", fontSize: "13px" }}
              >
                {i18n.t("FileModal.APIProviders.VideoGeneration.NoOverlaysYet")}
              </div>
            )}

            {textOverlays &&
              textOverlays.map((overlay, idx) => (
                <div
                  key={overlay.id || idx}
                  style={{
                    marginBottom: "12px",
                    padding: "12px",
                    background: "#fff",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  {/* Header: type badge + delete */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <span
                      style={{
                        padding: "2px 8px",
                        fontSize: "11px",
                        fontWeight: "600",
                        background: overlay.type === "text" ? "#dbeafe" : "#d1fae5",
                        color: overlay.type === "text" ? "#1e40af" : "#065f46",
                        borderRadius: "4px",
                      }}
                    >
                      {overlay.type === "text"
                        ? i18n.t("FileModal.APIProviders.VideoGeneration.Text").toUpperCase()
                        : i18n.t("FileModal.APIProviders.VideoGeneration.Image").toUpperCase()}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = textOverlays.filter((_, i) => i !== idx);
                        onTextOverlaysChange?.(updated);
                      }}
                      style={{
                        padding: "4px",
                        background: "#fee2e2",
                        color: "#ef4444",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <LuTrash2 size={14} />
                    </button>
                  </div>

                  {/* Primary fields */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px",
                      fontSize: "12px",
                    }}
                  >
                    {overlay.type === "text" ? (
                      <>
                        <div style={{ gridColumn: "1 / -1" }}>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "4px",
                              color: "#475569",
                              fontWeight: "500",
                            }}
                          >
                            {i18n.t("FileModal.APIProviders.VideoGeneration.Text")}
                          </label>
                          <input
                            type="text"
                            value={overlay.text || ""}
                            onChange={(e) => {
                              const updated = [...textOverlays];
                              updated[idx] = { ...updated[idx], text: e.target.value };
                              onTextOverlaysChange?.(updated);
                            }}
                            placeholder={i18n.t(
                              "FileModal.APIProviders.VideoGeneration.EnterTextToDisplay"
                            )}
                            style={inputStyle}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                          />
                        </div>
                        <div>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "4px",
                              color: "#475569",
                              fontWeight: "500",
                            }}
                          >
                            {i18n.t("FileModal.APIProviders.VideoGeneration.Position")}
                          </label>
                          <select
                            value={overlay.position}
                            onChange={(e) => {
                              const updated = [...textOverlays];
                              updated[idx] = { ...updated[idx], position: e.target.value };
                              onTextOverlaysChange?.(updated);
                            }}
                            style={selectStyle}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                          >
                            <option value="top-left">
                              {i18n.t("FileModal.APIProviders.VideoGeneration.TopLeft")}
                            </option>
                            <option value="top-center">
                              {i18n.t("FileModal.APIProviders.VideoGeneration.TopCenter")}
                            </option>
                            <option value="top-right">
                              {i18n.t("FileModal.APIProviders.VideoGeneration.TopRight")}
                            </option>
                            <option value="bottom-left">
                              {i18n.t("FileModal.APIProviders.VideoGeneration.BottomLeft")}
                            </option>
                            <option value="bottom-center">
                              {i18n.t("FileModal.APIProviders.VideoGeneration.BottomCenter")}
                            </option>
                            <option value="bottom-right">
                              {i18n.t("FileModal.APIProviders.VideoGeneration.BottomRight")}
                            </option>
                            <option value="center">
                              {i18n.t("FileModal.APIProviders.VideoGeneration.Center")}
                            </option>
                          </select>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ gridColumn: "1 / -1" }}>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "4px",
                              color: "#475569",
                              fontWeight: "500",
                            }}
                          >
                            {i18n.t("FileModal.APIProviders.VideoGeneration.ImageURLOrUpload")}
                          </label>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <input
                              type="text"
                              value={overlay.imageUrl || ""}
                              onChange={(e) => {
                                const updated = [...textOverlays];
                                updated[idx] = { ...updated[idx], imageUrl: e.target.value };
                                onTextOverlaysChange?.(updated);
                              }}
                              placeholder={
                                i18n.t(
                                  "FileModal.APIProviders.VideoGeneration.ImageUrlPlaceholder"
                                ) || "https://..."
                              }
                              style={{ ...inputStyle, flex: 1 }}
                              onFocus={handleFocus}
                              onBlur={handleBlur}
                            />
                            <input
                              type="file"
                              accept="image/*"
                              ref={(el) => {
                                if (el) overlayImageInputRefs.current[overlay.id || idx] = el;
                              }}
                              style={{ display: "none" }}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                // 500 MB üst sınır guard'ı — aşan dosyayı kabul etme.
                                if (isFileTooLarge(file)) {
                                  alert(fileTooLargeMessage(file));
                                  return;
                                }
                                if (file) {
                                  const updated = [...textOverlays];
                                  updated[idx] = { ...updated[idx], imageFile: file, imageUrl: "" };
                                  onTextOverlaysChange?.(updated);
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                overlayImageInputRefs.current[overlay.id || idx]?.click()
                              }
                              style={{
                                padding: "6px 12px",
                                fontSize: "11px",
                                background: "#f1f5f9",
                                border: "1px solid #e2e8f0",
                                borderRadius: "4px",
                                cursor: "pointer",
                              }}
                            >
                              {i18n.t("FileModal.APIProviders.VideoGeneration.Upload")}
                            </button>
                          </div>
                          {overlay.imageFile && (
                            <div style={{ marginTop: "4px", fontSize: "11px", color: "#64748b" }}>
                              {i18n.t("FileModal.APIProviders.VideoGeneration.File")}:{" "}
                              {overlay.imageFile.name}
                            </div>
                          )}
                        </div>
                        <div>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "4px",
                              color: "#475569",
                              fontWeight: "500",
                            }}
                          >
                            {i18n.t("FileModal.APIProviders.VideoGeneration.Position")}
                          </label>
                          <select
                            value={overlay.position}
                            onChange={(e) => {
                              const updated = [...textOverlays];
                              updated[idx] = { ...updated[idx], position: e.target.value };
                              onTextOverlaysChange?.(updated);
                            }}
                            style={selectStyle}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                          >
                            <option value="top-left">
                              {i18n.t("FileModal.APIProviders.VideoGeneration.TopLeft")}
                            </option>
                            <option value="top-center">
                              {i18n.t("FileModal.APIProviders.VideoGeneration.TopCenter")}
                            </option>
                            <option value="top-right">
                              {i18n.t("FileModal.APIProviders.VideoGeneration.TopRight")}
                            </option>
                            <option value="bottom-left">
                              {i18n.t("FileModal.APIProviders.VideoGeneration.BottomLeft")}
                            </option>
                            <option value="bottom-center">
                              {i18n.t("FileModal.APIProviders.VideoGeneration.BottomCenter")}
                            </option>
                            <option value="bottom-right">
                              {i18n.t("FileModal.APIProviders.VideoGeneration.BottomRight")}
                            </option>
                            <option value="center">
                              {i18n.t("FileModal.APIProviders.VideoGeneration.Center")}
                            </option>
                          </select>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Advanced settings toggle */}
                  <button
                    type="button"
                    onClick={() => toggleAdvanced(overlay.id || idx)}
                    style={{
                      marginTop: "8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      background: "none",
                      border: "none",
                      padding: "2px 0",
                      fontSize: "11px",
                      fontWeight: "600",
                      color: "#7c3aed",
                      cursor: "pointer",
                    }}
                  >
                    <LuChevronDown
                      size={13}
                      style={{
                        transform: advancedOpenIds.has(overlay.id || idx)
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s",
                      }}
                    />
                    {i18n.t("FileModal.APIProviders.VideoGeneration.AdvancedSettings") ||
                      "Gelişmiş Ayarlar"}
                  </button>

                  {/* Advanced fields */}
                  {advancedOpenIds.has(overlay.id || idx) && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "8px",
                        fontSize: "12px",
                        marginTop: "8px",
                        paddingTop: "8px",
                        borderTop: "1px solid #f1f5f9",
                      }}
                    >
                      {overlay.type === "text" ? (
                        <>
                          <div>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "4px",
                                color: "#475569",
                                fontWeight: "500",
                              }}
                            >
                              {i18n.t("FileModal.APIProviders.VideoGeneration.FontFamily")}
                            </label>
                            <select
                              value={overlay.fontFamily || "DejaVu Sans"}
                              onChange={(e) => {
                                const updated = [...textOverlays];
                                updated[idx] = { ...updated[idx], fontFamily: e.target.value };
                                onTextOverlaysChange?.(updated);
                              }}
                              style={selectStyle}
                              onFocus={handleFocus}
                              onBlur={handleBlur}
                            >
                              <option value="DejaVu Sans">DejaVu Sans</option>
                              <option value="DejaVu Serif">DejaVu Serif</option>
                              <option value="DejaVu Mono">DejaVu Mono</option>
                            </select>
                          </div>
                          <div>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "4px",
                                color: "#475569",
                                fontWeight: "500",
                              }}
                            >
                              {i18n.t("FileModal.APIProviders.VideoGeneration.FontStyle")}
                            </label>
                            <select
                              value={overlay.fontStyle || "normal"}
                              onChange={(e) => {
                                const updated = [...textOverlays];
                                updated[idx] = { ...updated[idx], fontStyle: e.target.value };
                                onTextOverlaysChange?.(updated);
                              }}
                              style={selectStyle}
                              onFocus={handleFocus}
                              onBlur={handleBlur}
                            >
                              <option value="normal">
                                {i18n.t("FileModal.APIProviders.VideoGeneration.Normal")}
                              </option>
                              <option value="bold">
                                {i18n.t("FileModal.APIProviders.VideoGeneration.Bold")}
                              </option>
                              <option value="italic">
                                {i18n.t("FileModal.APIProviders.VideoGeneration.Italic")}
                              </option>
                              <option value="bold-italic">
                                {i18n.t("FileModal.APIProviders.VideoGeneration.BoldItalic")}
                              </option>
                            </select>
                          </div>
                          <div>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "4px",
                                color: "#475569",
                                fontWeight: "500",
                              }}
                            >
                              {i18n.t("FileModal.APIProviders.VideoGeneration.FontSize")}
                            </label>
                            <input
                              type="number"
                              value={overlay.fontSize || 16}
                              onChange={(e) => {
                                const updated = [...textOverlays];
                                updated[idx] = {
                                  ...updated[idx],
                                  fontSize: Number(e.target.value),
                                };
                                onTextOverlaysChange?.(updated);
                              }}
                              style={inputStyle}
                              onFocus={handleFocus}
                              onBlur={handleBlur}
                            />
                          </div>
                          <div>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "4px",
                                color: "#475569",
                                fontWeight: "500",
                              }}
                            >
                              {i18n.t("FileModal.APIProviders.VideoGeneration.FontColor")}
                            </label>
                            <input
                              type="color"
                              value={overlay.fontColor || "#FFFFFF"}
                              onChange={(e) => {
                                const updated = [...textOverlays];
                                updated[idx] = { ...updated[idx], fontColor: e.target.value };
                                onTextOverlaysChange?.(updated);
                              }}
                              style={{
                                width: "100%",
                                height: "32px",
                                border: "1px solid #e2e8f0",
                                borderRadius: "4px",
                              }}
                            />
                          </div>
                          <div>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "4px",
                                color: "#475569",
                                fontWeight: "500",
                              }}
                            >
                              {i18n.t("FileModal.APIProviders.VideoGeneration.BGColor")}
                            </label>
                            <input
                              type="color"
                              value={overlay.backgroundColor || "#000000"}
                              onChange={(e) => {
                                const updated = [...textOverlays];
                                updated[idx] = { ...updated[idx], backgroundColor: e.target.value };
                                onTextOverlaysChange?.(updated);
                              }}
                              style={{
                                width: "100%",
                                height: "32px",
                                border: "1px solid #e2e8f0",
                                borderRadius: "4px",
                              }}
                            />
                          </div>
                          <div>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "4px",
                                color: "#475569",
                                fontWeight: "500",
                              }}
                            >
                              {i18n.t("FileModal.APIProviders.VideoGeneration.BGOpacity")}
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="1"
                              step="0.1"
                              value={overlay.backgroundOpacity ?? 0.7}
                              onChange={(e) => {
                                const updated = [...textOverlays];
                                updated[idx] = {
                                  ...updated[idx],
                                  backgroundOpacity: Number(e.target.value),
                                };
                                onTextOverlaysChange?.(updated);
                              }}
                              style={inputStyle}
                              onFocus={handleFocus}
                              onBlur={handleBlur}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "4px",
                                color: "#475569",
                                fontWeight: "500",
                              }}
                            >
                              {i18n.t("FileModal.APIProviders.VideoGeneration.WidthPx")}
                            </label>
                            <input
                              type="number"
                              value={overlay.width || 150}
                              onChange={(e) => {
                                const updated = [...textOverlays];
                                updated[idx] = { ...updated[idx], width: Number(e.target.value) };
                                onTextOverlaysChange?.(updated);
                              }}
                              style={inputStyle}
                              onFocus={handleFocus}
                              onBlur={handleBlur}
                            />
                          </div>
                          <div>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "4px",
                                color: "#475569",
                                fontWeight: "500",
                              }}
                            >
                              {i18n.t("FileModal.APIProviders.VideoGeneration.HeightPx")}
                            </label>
                            <input
                              type="number"
                              value={overlay.height || 50}
                              onChange={(e) => {
                                const updated = [...textOverlays];
                                updated[idx] = { ...updated[idx], height: Number(e.target.value) };
                                onTextOverlaysChange?.(updated);
                              }}
                              style={inputStyle}
                              onFocus={handleFocus}
                              onBlur={handleBlur}
                            />
                          </div>
                          <div>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "4px",
                                color: "#475569",
                                fontWeight: "500",
                              }}
                            >
                              {i18n.t("FileModal.APIProviders.VideoGeneration.Opacity")}
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="1"
                              step="0.05"
                              value={overlay.opacity ?? 0.95}
                              onChange={(e) => {
                                const updated = [...textOverlays];
                                updated[idx] = { ...updated[idx], opacity: Number(e.target.value) };
                                onTextOverlaysChange?.(updated);
                              }}
                              style={inputStyle}
                              onFocus={handleFocus}
                              onBlur={handleBlur}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}

            {textOverlays && textOverlays.length > 0 && (
              <div style={{ marginTop: "16px" }}>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "8px",
                  }}
                >
                  {i18n.t("FileModal.APIProviders.VideoGeneration.Preview")}
                </div>
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "16/9",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                >
                  {/* Sample video background pattern */}
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      opacity: 0.1,
                      backgroundImage:
                        "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)",
                    }}
                  />

                  {/* Overlays */}
                  {textOverlays.map((overlay, idx) => {
                    const positionStyles = {
                      "top-left": { top: "16px", left: "16px" },
                      "top-center": { top: "16px", left: "50%", transform: "translateX(-50%)" },
                      "top-right": { top: "16px", right: "16px" },
                      "bottom-left": { bottom: "16px", left: "16px" },
                      "bottom-center": {
                        bottom: "16px",
                        left: "50%",
                        transform: "translateX(-50%)",
                      },
                      "bottom-right": { bottom: "16px", right: "16px" },
                      center: { top: "50%", left: "50%", transform: "translate(-50%, -50%)" },
                    };

                    const overlayStyle = {
                      position: "absolute",
                      ...(positionStyles[overlay.position] || positionStyles["bottom-left"]),
                      zIndex: idx + 1,
                    };

                    if (overlay.type === "text") {
                      const bgColor = overlay.backgroundColor || "#000000";
                      const bgOpacity = overlay.backgroundOpacity ?? 0.7;

                      // Convert hex to rgba
                      const hexToRgba = (hex, alpha) => {
                        const r = parseInt(hex.slice(1, 3), 16);
                        const g = parseInt(hex.slice(3, 5), 16);
                        const b = parseInt(hex.slice(5, 7), 16);
                        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                      };

                      const fontStyleMap = {
                        normal: { fontWeight: "normal", fontStyle: "normal" },
                        bold: { fontWeight: "bold", fontStyle: "normal" },
                        italic: { fontWeight: "normal", fontStyle: "italic" },
                        "bold-italic": { fontWeight: "bold", fontStyle: "italic" },
                      };

                      const fontFamilyMap = {
                        "DejaVu Sans": "Arial, sans-serif",
                        "DejaVu Serif": "Georgia, serif",
                        "DejaVu Mono": "'Courier New', monospace",
                      };

                      const appliedFontStyle =
                        fontStyleMap[overlay.fontStyle || "normal"] || fontStyleMap["normal"];
                      const appliedFontFamily =
                        fontFamilyMap[overlay.fontFamily || "DejaVu Sans"] ||
                        fontFamilyMap["DejaVu Sans"];

                      return (
                        <div key={overlay.id || idx} style={overlayStyle}>
                          <div
                            style={{
                              padding: "8px 12px",
                              fontSize: `${Math.round((overlay.fontSize || 40) * previewScale)}px`,
                              color: overlay.fontColor || "#FFFFFF",
                              backgroundColor: hexToRgba(bgColor, bgOpacity),
                              borderRadius: "4px",
                              fontFamily: appliedFontFamily,
                              ...appliedFontStyle,
                              whiteSpace: "nowrap",
                              maxWidth: "300px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {overlay.text ||
                              i18n.t("FileModal.APIProviders.VideoGeneration.SampleText")}
                          </div>
                        </div>
                      );
                    } else if (overlay.type === "image") {
                      const imageUrl = overlay.imageFile
                        ? URL.createObjectURL(overlay.imageFile)
                        : overlay.imageUrl;

                      if (!imageUrl) {
                        return (
                          <div key={overlay.id || idx} style={overlayStyle}>
                            <div
                              style={{
                                width: `${Math.round((overlay.width || 180) * previewScale)}px`,
                                height: `${Math.round((overlay.height || 100) * previewScale)}px`,
                                background: "rgba(255,255,255,0.2)",
                                border: "2px dashed rgba(255,255,255,0.4)",
                                borderRadius: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "11px",
                                color: "#fff",
                                opacity: overlay.opacity ?? 0.95,
                              }}
                            >
                              <LuImage size={20} />
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={overlay.id || idx} style={overlayStyle}>
                          <img
                            src={imageUrl}
                            alt=""
                            style={{
                              width: `${Math.round((overlay.width || 180) * previewScale)}px`,
                              height: `${Math.round((overlay.height || 100) * previewScale)}px`,
                              objectFit: "cover",
                              borderRadius: "4px",
                              opacity: overlay.opacity ?? 0.95,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                            }}
                          />
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="add-video-modal__ai-section">
        <div className="add-video-modal__ai-prompt-toggle">
          <button
            type="button"
            className={promptMode === "write" ? "active" : ""}
            onClick={() => {
              setPromptMode("write");
              onNarrationChange("");
            }}
          >
            <LuPenLine /> {i18n.t("FileModal.APIProviders.VideoGeneration.WritePrompt")}
          </button>
          <button
            type="button"
            className={promptMode === "narration" ? "active" : ""}
            onClick={async () => {
              setPromptMode("narration");
              // navId = sahne sesleri panelinde tıklanan sahne; yoksa aktif sahne.
              const sceneId = navId || navItemSelected;
              if (sceneId) {
                try {
                  const data = await SceneAudioService.getSceneAudios();
                  const scenes = Array.isArray(data?.scenes) ? data.scenes : [];
                  const scene = scenes.find((s) => s.navId === sceneId);
                  const audios = Array.isArray(scene?.audios) ? scene.audios : [];
                  // Metin narrationTextOriginal'da olmayabilir (yalnızca TTS üretiminde
                  // dolar); görsel/infografik modalları gibi narrationText ve voiceText'e
                  // de bak, ilk satır yerine metni olan sesi seç.
                  const textOf = (a) =>
                    a?.narrationTextOriginal || a?.narrationText || a?.voiceText || "";
                  const withText = audios.find((a) => textOf(a));
                  if (withText) {
                    onNarrationChange(textOf(withText).replace(/<[^>]*>/g, ""));
                  }
                } catch (error) {
                  console.error("Error loading narration text:", error);
                }
              }
            }}
          >
            <LuFileText /> {i18n.t("FileModal.APIProviders.VideoGeneration.FromNarration")}
          </button>
          {/* AI ile Zenginleştir toggle - şimdilik gizli */}
        </div>

        {promptMode === "write" ? (
          <>
            <div className="add-video-modal__ai-scene-tabs">
              {scenes.map((scene, idx) => (
                <button
                  key={scene.id}
                  type="button"
                  className={`add-video-modal__ai-scene-tab${
                    activeSceneIndex === idx ? " add-video-modal__ai-scene-tab--active" : ""
                  }`}
                  onClick={() => setActiveSceneIndex(idx)}
                >
                  {i18n.t("FileModal.APIProviders.VideoGeneration.Scene") || "Sahne"} {idx + 1}
                  {scenes.length > 1 && (
                    <span
                      className="add-video-modal__ai-scene-tab-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeScene(idx);
                      }}
                    >
                      <LuX size={11} />
                    </span>
                  )}
                </button>
              ))}
              <button
                type="button"
                className="add-video-modal__ai-scene-tab add-video-modal__ai-scene-tab--add"
                onClick={addScene}
              >
                <LuPlus size={14} />
              </button>
            </div>
            <textarea
              className="add-video-modal__ai-prompt-input"
              value={scenes[activeSceneIndex]?.text || ""}
              onChange={(e) => updateSceneText(activeSceneIndex, e.target.value)}
              placeholder={
                i18n.t("FileModal.APIProviders.VideoGeneration.DescribeVideo") ||
                "Bu sahne için metni yazın..."
              }
            />
          </>
        ) : (
          <div className="add-video-modal__ai-narration">
            <div className="add-video-modal__ai-narration-header">
              <span>
                <LuFileText /> {i18n.t("FileModal.APIProviders.VideoGeneration.SceneNarration")}
              </span>
              <span>{i18n.t("FileModal.APIProviders.VideoGeneration.EachSentenceOneScene")}</span>
            </div>
            <textarea
              className="add-video-modal__ai-narration-input"
              value={narrationText}
              onChange={(event) => onNarrationChange(event.target.value)}
              placeholder={i18n.t("FileModal.APIProviders.VideoGeneration.DescribeSceneNarration")}
            />
            <div className="add-video-modal__ai-narration-hint">
              {i18n.t("FileModal.APIProviders.VideoGeneration.EachSentenceBecomesScene")}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoAIGenerateTab;
